import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HELP_FEATURES } from '@/components/help/helpData';
import { comboOfEvent, highlightTarget } from '@/components/help/shortcutKeys';

const STORAGE_KEY = 'help-center.shortcut-overrides';

function readAssignments(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Fires the user-assigned help-center shortcuts app-wide: a captured combo
 * navigates to the feature's route and highlights its target, exactly like
 * pressing the feature's card in the help center does. Assignments live in
 * localStorage (`help-center.shortcut-overrides`) and are read per keypress,
 * so edits in the help center apply immediately.
 */
export function useShortcutDispatcher(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || isEditableTarget(event.target)) return;

      const combo = comboOfEvent(event);
      const featureId = readAssignments()[combo];
      if (!featureId) return;

      const feature = HELP_FEATURES.find((entry) => entry.id === featureId);
      if (!feature || (!feature.route && !feature.targetId)) return;

      event.preventDefault();
      if (feature.route) navigate(feature.route);
      if (feature.targetId) {
        const delay = feature.route ? 350 : 180;
        window.setTimeout(() => highlightTarget(feature.targetId as string), delay);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}
